/* eslint-disable max-len */
import React, {useEffect} from 'react';
import {useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Helmet} from 'react-helmet';

import styled, {css} from 'styled-components';

import {
    Flex,
    mobileMediaQuery,
    notWebMediaQuery,
    tabletMediaQuery,
    PageBodyWrapper,
    useIsMobile,
    useBreakpoint,
} from './../styles';

import Tabs from './../components/Tabs';
import Loader from './../components/Loader';
import ShowsGrid from './../components/ShowsGrid';
import VideosGrid from './../components/VideosGrid';

import EmptyAll from './../assets/fav_no_data.png';
import EmptyShows from './../assets/fav_no_shows.png';
import EmptyEpisodes from './../assets/fav_no_episodes.png';
import HeartIcon from '../assets/favourites_icon.png';
import {slugify} from '../helpers';
import LeftDecorator from './../assets/reface_left_side_decorator.png';
import RightDecorator from './../assets/reface_right_side_decorator.png';

const Container = styled.div`
    padding: 0px 0px 32px 0;
    min-height: calc(100vh - 392px);
    max-width: 1440px;
    align-self: center;
    width: 100%;
    background-color: black;

    ${notWebMediaQuery} {
        margin-top: 78px;
        min-height: initial;
    }

    ${tabletMediaQuery} {
        padding: 0px;
    }

    ${mobileMediaQuery} {
        padding: 0px;
    }
`;

const Col = styled.div`
    display: flex;
    flex-direction: column;
    width: ${props => props.width && props.width};
    margin: ${props => props.margin && props.margin};
    flex: ${props => props.flex && props.flex};
    justify-content: ${props => props.justifyContent && props.justifyContent};
    align-items: ${props => props.alignItems && props.alignItems};
    max-width: ${props => props.maxWidth && props.maxWidth};
    ${props =>
        props.padding &&
        css`
            padding: ${props.padding};
        `}
`;

const Title = styled.h1`
    font-family: 'TTNormsProExtraBlack';
    color: #fff;
    font-size: 24px;
    line-height: 30px;
    margin: 0;
    padding: 0;
    margin-bottom: 18px;
    text-align: center;
    max-width: 484px;
    align-self: center;

    ${mobileMediaQuery} {
        margin-bottom: 32px;
        font-size: 20px;
        line-height: 26px;

        text-align: center;
        margin-left: auto;
        margin-right: auto;
    }
`;

const Description = styled.p`
    color: #fff;
    font-size: 18px;
    line-height: 24px;
    margin: 0;
    padding: 0;
    width: calc(100% - 18px);
    margin-top: 21px;
    align-self: center;
    max-width: 484px;
    text-align: center;

    ${mobileMediaQuery} {
        margin-top: 32px;
        font-size: 16px;
        line-height: 22px;

        text-align: center;
        margin-left: auto;
        margin-right: auto;
    }
`;

const EmptyContentImage = styled.img`
    width: ${props => props.width && props.width};
    max-width: ${props => props.maxWidth && props.maxWidth};
    object-fit: contain;
    background-image: url(${LeftDecorator}), url(${RightDecorator});
    background-size: contain, contain;
    background-position: left, right;
    background-repeat: no-repeat;
    max-height: ${props => props.maxHeight && props.maxHeight};

    ${mobileMediaQuery} {
        background-size: 110px 157px, 194px 229px;
    }

    ${({notMobile}) =>
        notMobile &&
        css`
            ${mobileMediaQuery} {
                display: none;
            }
        `}
`;

const TabContentWrapper = styled(Flex)`
    flex-direction: row;

    ${mobileMediaQuery} {
        flex-direction: column;
    }
`;

const Favourites = props => {
    const history = useHistory();
    const {t} = useTranslation();

    const {user} = useSelector(state => state);
    const {shows, episodes} = useSelector(state => state.favourite);

    const idp = user && user.idp;

    const breakpoint = useBreakpoint();
    const isMobile = ['xs', 'mobile', 'sm', 'md'].includes(breakpoint);

    useEffect(() => {
        window.turner_metadata.trackAction.push({
            type: 'pageview',
            data: {
                pageName: 'cnplus.com/favourites',
                section: 'Favorites',
                subsection: 'nvs',
                contenttype: 'Favorites',
                contenttitle: 'Favorites',
                presentationtemplate: 'desktop',
                platform: 'web',
                domain: 'cnplus.com',
                region: 'latam',
                country:
                    user && user.countryIsoCode && user.countryName
                        ? `${user.countryIsoCode.toLowerCase()}|${
                              user.countryName
                          }`
                        : 'not loaded',
                brand: 'cartoon network',
                pageviewevent: 1,
                authstate:
                    user && user.jwtToken ? 'authenticated' : 'unauthenticated',
                mvpd: idp || 'idp not loaded',
            },
        });
        // eslint-disable-next-line
    }, []);

    const handleClickIp = show_title => {
        history.push(`/shows/${slugify(show_title)}`);
    };

    const handleClickEp = content_id => {
        history.push(`/play/${content_id}`);
    };

    const renderShowTab = React.useMemo(() => {
        return shows === undefined ? (
            <Loader />
        ) : shows.length === 0 ? (
            <TabContentWrapper
                paddingMaxBreakpoint="1440"
                direction={'column'}
                margin={'0 0 40px 0'}
            >
                <Col flex="1" margin={'0px auto 30px auto'} width="100%">
                    <Title>{t('favorites.noShowTitle')}</Title>
                    <Flex>
                        <EmptyContentImage
                            width="100%"
                            maxHeight="321px"
                            src={EmptyShows}
                            alt="No favorite shows"
                        />
                    </Flex>
                    <Description>
                        {t('favorites.description1')}{' '}
                        <img alt="Heart" height="15px" src={HeartIcon} />{' '}
                        {t('favorites.description2')}
                    </Description>
                </Col>
            </TabContentWrapper>
        ) : (
            <ShowsGrid
                items={shows}
                onClickItem={handleClickIp}
                isFavouritesGrid
            />
        );

        // eslint-disable-next-line
    }, [shows.length === 0, isMobile]);

    const renderEpisodesTab = React.useMemo(() => {
        return episodes === undefined ? (
            <Loader />
        ) : episodes.length === 0 ? (
            <Flex direction={isMobile ? 'column' : 'row'}>
                <Col flex="1" margin={'0px auto 30px auto'} width="100%">
                    <Title>{t('favorites.noEpisodeTitle')}</Title>
                    <Flex>
                        <EmptyContentImage
                            width="100%"
                            maxHeight="321px"
                            src={EmptyEpisodes}
                            alt="No favorite episodes"
                        />
                    </Flex>
                    <Description>
                        {t('favorites.description1')}{' '}
                        <img alt="Heart" height="15px" src={HeartIcon} />{' '}
                        {t('favorites.description2')}
                    </Description>
                </Col>
            </Flex>
        ) : (
            <VideosGrid
                items={episodes}
                onClickItem={handleClickEp}
                isFavouritesGrid
            />
        );

        // eslint-disable-next-line
    }, [episodes.length === 0, isMobile]);

    return (
        <>
            <Helmet>
                <title>Cartoon Network App</title>
            </Helmet>

            {shows &&
            shows.length === 0 &&
            episodes &&
            episodes.length === 0 ? (
                <Container>
                    <TabContentWrapper>
                        <Col flex="1" margin={isMobile ? '30px 0px 30px 0px' : '100px 0px 30px 0px'} width="100%">
                            <Title>{t('favorites.noContent')}</Title>
                            <Flex>
                                <EmptyContentImage
                                    width="100%"
                                    maxHeight="321px"
                                    src={EmptyAll}
                                    alt="No favorites"
                                />
                            </Flex>
                            <Description>
                                {t('favorites.description1')}{' '}
                                <img
                                    alt="Heart"
                                    height="15px"
                                    src={HeartIcon}
                                />{' '}
                                {t('favorites.description2')}
                            </Description>
                        </Col>
                    </TabContentWrapper>
                </Container>
            ) : (
                <Container>
                    <Tabs
                        tabColor='#211921'
                        items={[
                            {
                                title: t('favorites.shows'),
                                content: (
                                    <PageBodyWrapper fluid>
                                        {renderShowTab}
                                    </PageBodyWrapper>
                                ),
                            },
                            {
                                title: t('favorites.episodes'),
                                content: (
                                    <PageBodyWrapper fluid>
                                        {renderEpisodesTab}
                                    </PageBodyWrapper>
                                ),
                            },
                        ]}
                    />
                </Container>
            )}
        </>
    );
};

export default Favourites;
